<template>
  <div>
    <AttachmentUploader
      v-show="false"
      ref="attachmentUploader"
      @valid="emitAttachmentsValid"
    />
    <ForumWysiwyg
      v-model="editorValue"
      :placeholder="placeholder"
      :getMentionables="getMentionables"
      :inline="inline"
      :disabled="disabled"
      @retry-upload="retryUploadingAttachment"
      @cancelUpload="cancelUpload"
    >
      <template v-slot:default="slotProps">
        <slot v-bind="slotProps" />
      </template>
    </ForumWysiwyg>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, provide, Ref, ref, watch } from "vue"
import AttachmentUploader from "features/attachments/AttachmentUploader.vue"
import { SuggestionFunction } from "components/Wysiwyg/extensions/suus-mention/suus-mention.config"
import {
  AttachmentMediaUpload,
  attachmentToAttachmentMediaUpload,
} from "features/attachments/attachment.types"
import ForumWysiwyg from "components/Wysiwyg/ForumWysiwyg.vue"
import { Attachment } from "suus-api"

export default defineComponent({
  name: "ForumWysiwygWithUploader",
  components: { ForumWysiwyg, AttachmentUploader },
  props: {
    value: { type: String, default: "" },
    showControls: { type: Boolean, default: true },
    placeholder: {
      type: String,
      default:
        "Schreibe hier Deinen Beitrag. Du kannst Dateien und Fotos direkt hier hineinziehen oder kopieren, um sie einzubinden. Erwähne Personen, Ämter oder Gruppen mit einem @-Zeichen.",
    },
    inline: { type: Boolean, default: false }, // note that this prop is not reactive, because it is set during mount
    getMentionables: {
      type: Function as PropType<SuggestionFunction>,
      default: (_) => [],
    },
    loadAttachment: {
      type: Function as PropType<(frontendId: string) => Promise<Attachment>>,
    },
    disabled: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const editorValue = ref(props.value)
    watch(
      () => props.value,
      (v) => (editorValue.value = v)
    )
    watch(editorValue, (v) => emit("input", v))
    const attachmentUploader =
      ref<InstanceType<typeof AttachmentUploader>>(null)

    const uploadedAttachments = ref<Ref<AttachmentMediaUpload>[]>([])

    const retryUploadingAttachment = (frontendId: string) => {
      const token = uploadedAttachments.value.find(
        (u) => u.value.frontendId === frontendId
      )?.value.file.token
      if (token) {
        attachmentUploader.value?.retryUpload(token)
      } else {
        console.warn("No token found while trying reloading attachment.")
      }
    }

    // Adds the file to the upload-list and afterwards performs the upload
    const uploadFile = (file: File): Ref<AttachmentMediaUpload> => {
      const ret = attachmentUploader.value.manuallyAddFileAndUpload(file)
      uploadedAttachments.value.push(ret)
      return ret
    }

    provide("uploadFile", uploadFile)
    provide(
      "loadAttachmentByFrontendId",
      (frontendId: string): Ref<AttachmentMediaUpload> => {
        const existingUpload = uploadedAttachments.value.find(
          (u) => u.value.frontendId === frontendId
        )
        if (existingUpload) {
          return existingUpload
        } else {
          const refAtt = ref<AttachmentMediaUpload>(undefined)
          props.loadAttachment(frontendId).then((a: Attachment) => {
            refAtt.value = attachmentToAttachmentMediaUpload(a)
          })
          return refAtt
        }
      }
    )

    const cancelUpload = (frontendId: string) => {
      attachmentUploader.value?.manuallyDeleteUpload(frontendId)
    }

    const emitAttachmentsValid = (valid: boolean) =>
      emit("attachments-valid", valid)

    return {
      attachmentUploader,
      emitAttachmentsValid,
      retryUploadingAttachment,
      cancelUpload,
      editorValue,
      uploadedAttachments,
    }
  },
})
</script>
